import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { ErreurApi, normaliserErreurHttp } from '../../Services/api-error';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormField, form, maxLength, required } from '@angular/forms/signals';
import { MatDialogRef } from '@angular/material/dialog';
import { Installation as InstallationService } from '../../Services/installation';
import { Installation, Parc } from '../../Services/EntiteProjet';
import { ParcService } from '../../Services/parc';
import { EtatService } from '../../Services/etat';
import { Authentification } from '../../Services/authentification';

@Component({
  selector: 'app-creer-parc-dialog',
  imports: [FormField],
  templateUrl: './creer-parc-dialog.html',
  styleUrl: './creer-parc-dialog.css',
})
export class CreerParcDialog {
  private readonly dialog = inject(MatDialogRef<CreerParcDialog, Parc>);
  private readonly installationService = inject(InstallationService);
  private readonly parcService = inject(ParcService);
  private readonly etat = inject(EtatService);
  private readonly auth = inject(Authentification);
  private readonly destroyRef = inject(DestroyRef);
  private readonly session = this.auth.sessionVersion;

  readonly modele = signal({ libelle: '' });
  readonly formulaire = form(this.modele, schema => {
    required(schema.libelle);
    maxLength(schema.libelle, 100);
  });
  readonly recherche = signal('');
  readonly installations = signal<Installation[]>([]);
  readonly selection = signal<Set<number>>(new Set());
  readonly chargement = signal(true);
  readonly envoi = signal(false);
  readonly erreurChargement = signal('');
  readonly erreur = signal('');
  readonly installationsFiltrees = computed(() => {
    const recherche = this.normaliser(this.recherche().trim());
    return this.installations().filter(i => this.normaliser(i.nom).includes(recherche));
  });
  readonly peutCreer = computed(() => !this.chargement() && !this.erreurChargement() &&
    !this.envoi() && !this.formulaire().invalid() && this.modele().libelle.trim().length > 0 &&
    this.selection().size > 0);

  constructor() {
    this.auth.sessionChanges.pipe(takeUntilDestroyed()).subscribe(() => this.dialog.close());
    void this.chargerInstallations();
  }

  private normaliser(texte: string): string {
    return texte.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('fr');
  }

  private estActif(): boolean {
    return !this.destroyRef.destroyed && this.session === this.auth.sessionVersion;
  }

  async chargerInstallations(): Promise<void> {
    this.chargement.set(true);
    this.erreurChargement.set('');
    try {
      const global = this.etat.parcs().find(p => p.global);
      if (!global) throw new Error('Parc global indisponible.');
      const installations = await this.installationService.listeInstallations(global.id);
      if (!installations) throw new Error('Liste indisponible.');
      if (this.estActif()) this.installations.set([...installations].sort((a, b) => a.nom.localeCompare(b.nom)));
    } catch {
      if (this.estActif()) this.erreurChargement.set('Impossible de charger les installations. Réessayez.');
    } finally {
      if (this.estActif()) this.chargement.set(false);
    }
  }

  basculerInstallation(id: number): void {
    if (this.envoi()) return;
    this.selection.update(selection => {
      const suivante = new Set(selection);
      if (suivante.has(id)) suivante.delete(id);
      else suivante.add(id);
      return suivante;
    });
  }

  fermer(): void {
    if (!this.envoi()) this.dialog.close();
  }

  async creer(event: Event): Promise<void> {
    event.preventDefault();
    if (!this.peutCreer()) return;
    this.envoi.set(true);
    this.erreur.set('');
    this.dialog.disableClose = true;
    try {
      const parc = await this.parcService.creerParc({
        libelle: this.modele().libelle.trim(),
        installation_ids: [...this.selection()],
      });
      if (this.estActif()) {
        this.etat.ajouterParcCree(parc);
        this.dialog.close(parc);
      }
    } catch (error: unknown) {
      if (this.estActif()) this.erreur.set(this.messageErreur(error));
    } finally {
      if (this.estActif()) {
        this.envoi.set(false);
        this.dialog.disableClose = false;
      }
    }
  }

  private messageErreur(error: unknown): string {
    error = normaliserErreurHttp(error);
    if (error instanceof ErreurApi) {
      switch (error.status) {
        case 0: return 'Connexion interrompue. Vérifiez la liste des parcs avant de réessayer.';
        case 401: return 'Votre session a expiré. Reconnectez-vous.';
        case 403: return 'Vous ne pouvez pas créer ce parc avec les installations sélectionnées.';
        case 409: return 'Un parc portant ce nom existe déjà. Choisissez un autre libellé.';
        case 400:
        case 422: return 'Vérifiez le libellé et la sélection des installations.';
        case 404:
        case 405:
        case 501: return 'La création de parc n’est pas encore disponible. Réessayez plus tard.';
        default: return 'La création n’a pas pu être confirmée. Vérifiez la liste des parcs avant de réessayer.';
      }
    }
    return error instanceof Error ? error.message : 'La création du parc a échoué. Réessayez.';
  }
}
